import { ReactNode, useEffect, useState } from "react";
import actionsApi from "../services/actionsApi";
import { Row } from "../types";
import { rowText } from "../utils";
import FlagDetailsCollapse from "Pages/Requisition/SingleRequisition/ViewReq/FlagDetailsCollapse";
import InternalNotesCollapse from "Pages/Requisition/SingleRequisition/ViewReq/InternalNotesModal";
import useLang from "Shared/hooks/useLanguage";

interface ShellProps {
  icon: string;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}

// Side panel frame with the same 40px header as the details view; ✕ returns to details.
const PanelShell = ({ icon, title, subtitle, onClose, children }: ShellProps) => {
  const { t } = useLang();
  return (
    <aside className="vr2-detail" aria-label={title}>
      <div className="vr2-detail-head">
        <i className={`bi ${icon} fs-5 text-primary flex-shrink-0`}></i>
        <span className="vr2-head-name">{title}</span>
        {subtitle && (
          <>
            <span className="vr2-head-sep" aria-hidden="true" />
            <span className="vr2-head-accession">{subtitle}</span>
          </>
        )}
        <button
          type="button"
          className="btn btn-icon btn-sm h-28px w-28px btn-active-light-primary ms-auto flex-shrink-0"
          aria-label={t("Back to details")}
          title={t("Back to details")}
          onClick={onClose}
        >
          <i className="bi bi-x-lg fs-6"></i>
        </button>
      </div>
      <div className="vr2-detail-body vr2-panel-embed">{children}</div>
    </aside>
  );
};

const subtitleOf = (row: Row) =>
  rowText(row, "AccessionNumber") || rowText(row, "Order");

// Internal notes (v1 InternalNotesCollapse, embedded).
export const NotesPanel = ({ row, onClose }: { row: Row; onClose: () => void }) => {
  const { t } = useLang();
  return (
    <PanelShell
      icon="bi-sticky"
      title={t("Internal Notes")}
      subtitle={subtitleOf(row)}
      onClose={onClose}
    >
      <InternalNotesCollapse open onClose={onClose} rowData={row} hideHeader />
    </PanelShell>
  );
};

// Flag details (GetRequisitionFlagDetails → v1 FlagDetailsCollapse, embedded).
export const FlagPanel = ({
  row,
  flagId,
  onClose,
}: {
  row: Row;
  flagId: number;
  onClose: () => void;
}) => {
  const { t } = useLang();
  const [data, setData] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setFailed(false);
    actionsApi
      .flagDetails(row, flagId)
      .then((d) => alive && setData(d))
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [row, flagId]);

  return (
    <PanelShell
      icon="bi-flag"
      title={t("Flag Details")}
      subtitle={subtitleOf(row)}
      onClose={onClose}
    >
      {failed ? (
        <div className="alert alert-danger py-2 my-3">
          {t("Something went wrong while loading flag details.")}
        </div>
      ) : (
        <FlagDetailsCollapse open onClose={onClose} data={data} isLoading={loading} />
      )}
    </PanelShell>
  );
};
